import React, { useEffect, useState } from 'react';
import { postApi } from 'apis';
import { ApprovalLayout } from 'layouts/ApprovalLayout';
import { useSearchParams } from 'react-router-dom';
import { toastText } from 'utils/utils';
import ApproveRejectComponent from 'components/Payroll/PayrollApproval';
import PayrollCommentForm from 'components/Payroll/PayrollCommentForm';
import { Card, Typography } from 'antd';

const { Title } = Typography;

const ApproveRejectPage: React.FC = () => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [status, setStatus] = useState<
		'idle' | 'approved' | 'rejected' | 'comments_submitted' | 'comment_form' | 'error' | ''
	>('');
	const [approvalRequestId, setApprovalRequestId] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');
	const action = searchParams.get('action');

	useEffect(() => {
		handleAction();
	}, []);

	const handleAction = async () => {
		setIsLoading(true);
		setStatus('idle');

		if (!token) {
			setIsLoading(false);
			return;
		}

		try {
			const isToken = await verifyToken(token);

			if (!isToken) {
				setStatus('error');
				return;
			}

			const approvalId = isToken.data.approvalId;

			if (action === 'approve') {
				await approvePayroll(approvalId);
			} else if (action === 'request-changes') {
				setApprovalRequestId(approvalId);
				setStatus('comment_form');
			} else {
				setStatus('error');
			}
		} catch (error: any) {
			setStatus('error');
			toastText(
				'An unexpected error occurred. Please try again.',
				'error'
			);
		} finally {
			setIsLoading(false);
		}
	};

	const verifyToken = async (tokenParam: string) => {
		try {
			const isToken = await postApi(
				`/auth/payroll-approval-verify-token?token=${tokenParam}`
			);
			return isToken;
		} catch (error: any) {
			let errorMessage = '';
			if (error.response?.data?.responseStatus === 401) {
				errorMessage = 'Token verification failed please login first';
			} else {
				errorMessage = error.response?.data?.message;
			}
			toastText(errorMessage, 'error');
			return null;
		}
	};

	const approvePayroll = async (approvalRequestIdParam: string) => {
		try {
			await postApi(`/payroll/approve`, { approvalRequestId: approvalRequestIdParam });
			setStatus('approved');
			toastText('Payroll approved successfully', 'success');
		} catch (error: any) {
			setStatus('error');
			const errorMessage =
				error.response?.data?.message || 'Failed to approve payroll';
			toastText(errorMessage, 'error');
		}
	};

	const handleSubmitComments = async (comments: string[]) => {
		if (!approvalRequestId) return;
		setIsLoading(true);
		try {
			await postApi('/payroll/add-comments', {
				approvalRequestId,
				comments,
			});
			setStatus('comments_submitted');
			toastText('Comments submitted. Payroll has been sent back for revision.', 'success');
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || 'Failed to submit comments';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	if (status === 'comment_form') {
		return (
			<ApprovalLayout>
				<Card style={{ maxWidth: 520, margin: '20px auto' }}>
					<Title level={3} style={{ marginBottom: 16 }}>
						Request changes
					</Title>
					<p style={{ marginBottom: 16 }}>
						Add at least one comment. The payroll will be sent back to draft for revision.
					</p>
					<PayrollCommentForm
						onSubmit={handleSubmitComments}
						loading={isLoading}
						submitLabel="Submit comments"
					/>
				</Card>
			</ApprovalLayout>
		);
	}

	return (
		<ApprovalLayout>
			<ApproveRejectComponent
				isLoading={isLoading}
				status={status}
				errorMessage={''}
			/>
		</ApprovalLayout>
	);
};

export default ApproveRejectPage;
